import React, { useState, useEffect, useRef, useContext } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import { PackagesContext } from "../context/PackagesContext";
import { ThemeContext } from "../context/ThemeContext";

const HomePackages = ({ destinations = [], airportImages = [] }) => {
  const [hoveredPackage, setHoveredPackage] = useState(null);
  const [expandedDescriptions, setExpandedDescriptions] = useState({});
  const { packages } = useContext(PackagesContext);
  const { theme } = useContext(ThemeContext);
  const navigate = useNavigate();
  const containerRef = useRef(null);
  const scrollDirection = useRef("right");
  const scrollSpeed = useRef(2);
  const animationFrameId = useRef(null);
  const isScrolling = useRef(false);

  const displayNameMap = {
    UAE: "Dubai",
    Indonesia: "Bali",
  };

  const getDisplayName = (value) => displayNameMap[value] || value;

  const defaultDestinations = [
    { name: "Bali", image: "img-1.jpg", description: "Discover the stunning beaches and vibrant culture of Bali." },
    { name: "Thailand", image: "img-2.jpg", description: "Enjoy the exotic charm and amazing food in Thailand." },
    { name: "Maldives", image: "img-3.jpg", description: "Relax in paradise with Maldives holiday packages." },
    { name: "Sri Lanka", image: "img-4.jpg", description: "Explore the beauty and culture of Sri Lanka." },
    { name: "Dubai", image: "Dubai-1.jpg", description: "Book Dubai Holiday Tour Packages now from View Holiday Trip." },
    { name: "Hong Kong", image: "Dubai-2.jpg", description: "Explore Hong Kong with amazing holiday packages." },
    { name: "Singapore", image: "Dubai-3.jpg", description: "Book your Singapore tour packages today." },
    { name: "Malaysia", image: "footer.jpg", description: "Experience the beauty of Malaysia with our packages." },
  ];

  const destinationQueryMap = {
    UAE: { destination: null, country: "UAE" },
    Thailand: { destination: null, country: "Thailand" },
    "Sri Lanka": { destination: null, country: "Sri Lanka" },
    "Hong Kong": { destination: null, country: "Hong Kong" },
    Maldives: { destination: "Maldives", country: "Maldives" },
    Singapore: { destination: "Singapore", country: "Singapore" },
    Indonesia: { destination: "Bali", country: "Indonesia" },
    "Andaman & Nicobar": { destination: "Andaman & Nicobar", country: "India" },
    Kerala: { destination: "Kerala", country: "India" },
    Goa: { destination: "Goa", country: "India" },
  };

  const handleDestinationClick = (label) => {
    const mapEntry = destinationQueryMap[label];
    if (mapEntry) {
      const queryParams = new URLSearchParams();
      if (mapEntry.destination) queryParams.set("destination", mapEntry.destination);
      if (mapEntry.country) queryParams.set("country", mapEntry.country);
      navigate(`/all_packages?${queryParams.toString()}`);
    } else {
      console.warn(`No mapping found for: ${label}`);
    }
  };

  useEffect(() => {
    const handleWheel = (e) => {
      if (Math.abs(e.deltaY) > 0) {
        scrollDirection.current = e.deltaY > 0 ? "right" : "left";
        scrollSpeed.current = Math.min(Math.abs(e.deltaY) * 0.15, 8);
        isScrolling.current = true;
        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => {
          isScrolling.current = false;
        }, 500);
      }
    };

    const autoScroll = () => {
      if (!containerRef.current) return;
      const container = containerRef.current;
      const maxScroll = container.scrollWidth - container.clientWidth;

      if (isScrolling.current) {
        container.scrollLeft += scrollDirection.current === "right" ? scrollSpeed.current : -scrollSpeed.current;
        if (container.scrollLeft >= maxScroll) container.scrollLeft = 0;
        else if (container.scrollLeft <= 0) container.scrollLeft = maxScroll;
      } else {
        container.scrollLeft += scrollDirection.current === "right" ? 1 : -1;
        if (container.scrollLeft >= maxScroll) scrollDirection.current = "left";
        else if (container.scrollLeft <= 0) scrollDirection.current = "right";
      }

      animationFrameId.current = requestAnimationFrame(autoScroll);
    };

    window.addEventListener("wheel", handleWheel);
    animationFrameId.current = requestAnimationFrame(autoScroll);

    return () => {
      window.removeEventListener("wheel", handleWheel);
      cancelAnimationFrame(animationFrameId.current);
      clearTimeout(window.scrollTimeout);
    };
  }, []);

  const seenDestinations = new Set();
  const domesticPackages = packages
    .filter(pkg => pkg.international === false && pkg.destination)
    .filter(pkg => {
      if (seenDestinations.has(pkg.destination)) return false;
      seenDestinations.add(pkg.destination);
      return true;
    });

  const seenCountries = new Set();
  const internationalPackages = packages
    .filter(pkg => pkg.international !== false && pkg.country)
    .filter(pkg => {
      if (seenCountries.has(pkg.country)) return false;
      seenCountries.add(pkg.country);
      return true;
    });

  const toggleDescription = (index) => {
    setExpandedDescriptions((prev) => ({
      ...prev,
      [index]: !prev[index],
    }));
  };

  const renderSection = (title, data) => (
    <section className="max-w-7xl mx-auto mb-16">
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mb-12">
        <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">{title}</h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
          {title === "Domestic Destinations"
            ? "Explore amazing destinations within the country."
            : "Discover handpicked international locations offering unforgettable experiences."}
        </p>
      </motion.div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {data.map((destination, index) => {
          const label = title === "Domestic Destinations" ? destination.destination : destination.country;
          const displayText = destination.description || "";
          const isExpanded = expandedDescriptions[index];
          const shouldTruncate = displayText.length > 100;

          return (
            <motion.div
              key={index}
              whileHover="hover"
              initial="rest"
              animate="rest"
              className="relative group cursor-pointer rounded-xl overflow-hidden shadow-lg h-64"
              onClick={() => handleDestinationClick(label)}
            >
              <img
                src={destination.image1 || destination.image || "https://via.placeholder.com/400x300"}
                alt={getDisplayName(label)}
                className="w-full h-full object-cover transform transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent p-6 flex items-end">
                <h3 className="text-xl font-semibold text-white">
                  {title === "Domestic Destinations"
                    ? destination.destination
                    : getDisplayName(destination.country)}
                </h3>
              </div>
              <motion.div
                className="absolute inset-0 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm p-6 flex flex-col justify-center items-center"
                variants={{
                  hover: { opacity: 1 },
                  rest: { opacity: 0 },
                }}
                transition={{ duration: 0.3 }}
              >
                <h3 className="text-2xl font-bold text-blue-600 dark:text-blue-400 mb-3">
                  {title === "Domestic Destinations"
                    ? destination.destination
                    : getDisplayName(destination.country)}
                </h3>

                <p className="text-gray-600 dark:text-gray-300 text-center mb-5 text-sm">
                  {isExpanded || !shouldTruncate
                    ? displayText
                    : `${displayText.slice(0, 160)}... `}
                  {shouldTruncate && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleDescription(index);
                      }}
                      className="text-blue-600 dark:text-blue-400 font-medium ml-1 hover:underline"
                    >
                      {isExpanded ? "less" : "more"}
                    </button>
                  )}
                </p>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition-colors"
                >
                  Explore Packages
                </motion.button>
                <div className="flex gap-4 mt-4 text-blue-600 dark:text-blue-400">
                  <i className="fas fa-hotel text-lg hover:text-blue-700"></i>
                  <i className="fas fa-utensils text-lg hover:text-blue-700"></i>
                  <i className="fas fa-car text-lg hover:text-blue-700"></i>
                  <i className="fas fa-camera text-lg hover:text-blue-700"></i>
                </div>
              </motion.div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );

  return (
    <div className={`${theme === "dark" ? "dark" : ""}`}>
      <div className="bg-gradient-to-b from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900 py-16 px-4 sm:px-6 lg:px-8">
        {renderSection(
          "International Destinations",
          internationalPackages.length ? internationalPackages : defaultDestinations
        )}
        {renderSection("Domestic Destinations", domesticPackages)}
      </div>
    </div>
  );
};

HomePackages.propTypes = {
  destinations: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string.isRequired,
      image: PropTypes.string.isRequired,
      description: PropTypes.string.isRequired,
    })
  ),
  airportImages: PropTypes.arrayOf(PropTypes.string),
};

export default HomePackages;
